
export const caseStudies = [
  {
    id: 1,
    title: "Opportunity Feed Card",
    category: "Web Design",
    description:
      "Redesigned the opportunity discovery experience for a legal recruitment platform, focusing on improving user engagement and conversion rates through better information hierarchy and visual design.",
    image:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/f2bd20388b2a08f302e6e978cc0405e9.png",
    tags: [
      "Legal Tech UX",
      "UX Research",
      "User Flows",
      "User Interviews",
      "Wireframes",
      "Card Design",
      "Visual Design",
      "Design System",
    ],
    duration: "4 months",
    role: "Lead Product Designer",
    year: "2023",
    challenge:
      "After the Opportunity Feed was introduced, talent began applying to roles they weren't qualified for, creating strain on Talent Services' ability to respond to their needs within 24 hours.",
    solution:
      "Redesign the Opportunity Feed cards to highlight the most relevant information, enabling talent to make informed expressions of interest and maximize their chances of landing the role.",
    results:
      "There was a reduction in talent applying to opportunities late and a slight drop in the total number of applications per talent.",
    axiomNewFlow: {
      description:
        "Axiom connects legal teams with top talent for everything from everyday in-house needs to complex counsel work. Over time, it has evolved from an analog service to a digital platform while preserving its white-glove experience. The Opportunity Feed marked the first step in empowering lawyers to take control of their careers.",
      images: [
        {
          id: "axiom-flow-before",
          url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/298f986c00af6002bb9f351f4499ba37.png",
          title: "Talent Services Process Changes",
          description:
            "With the introduction of the Opportunity Feed, Talent Services began listing opportunities directly, allowing talent to express interest proactively rather than waiting for Talent Success to present opportunities. This shift required Talent Services to manage and remediate these incoming expressions of interest.",
        },
        {
          id: "axiom-flow-after",
          url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8ccb4deef482a40694a223f7d2a63d22.png",
          title: "User Flows",
          description:
            "Reviewed and updated the master user flow to uncover issues and identify solutions resulting from the introduction of the Opportunity Feed.",
        },
      ],
    },
    processImages: [
      {
        id: "axiom-research",
        url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/298f986c00af6002bb9f351f4499ba37.png",
        caption: "User Interviews & Discovery",
        description:
          "Conducted interviews with 15 legal professionals to understand their job search pain points and current platform usage patterns.",
      },
      {
        id: "axiom-wireframes",
        url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8ccb4deef482a40694a223f7d2a63d22.png",
        caption: "User Interviews",
        description:
          "Created low-fidelity wireframes focusing on information hierarchy and card layout optimization for better decision-making.",
      },
      {
        id: "axiom-prototypes",
        url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/8d89e211b26dad1f954b94093a3071cf.png",
        caption: "High-Fidelity Prototypes",
        description:
          "Developed interactive prototypes with improved information hierarchy, clearer qualification requirements, and enhanced visual design.",
      },
      {
        id: "axiom-testing",
        url: "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/22b81e9fb21c675a6364daa076c0a825.png",
        caption: "Final Design",
        description:
          "Conducted usability testing with 12 legal professionals to validate design decisions and measure comprehension improvements.",
      },
    ],
    processSteps: [
      {
        title: "Research & Discovery",
        description:
          "I began by conducting comprehensive user research to understand the pain points lawyers were experiencing with the existing Opportunity Feed. Through interviews and data analysis, I identified that talent were applying to roles without fully understanding the requirements.",
        insights: [
          "67% of applications were from unqualified candidates",
          "Talent Services response time increased by 40%",
          "User confusion around role requirements was the primary issue",
        ],
      },
      {
        title: "Problem Definition",
        description:
          "The core problem was information hierarchy - critical qualification details were buried or unclear, leading to poor decision-making by talent when expressing interest in opportunities.",
        insights: [
          "Key requirements were not prominently displayed",
          "Visual hierarchy didn't guide user attention effectively",
          "Cognitive load was too high for quick decision-making",
        ],
      },
      {
        title: "Design Solution",
        description:
          "I redesigned the card structure to prioritize essential information, using visual hierarchy and progressive disclosure to help talent make informed decisions quickly and accurately.",
        insights: [
          "Moved key qualifications to the top of each card",
          "Implemented clear visual indicators for match quality",
          "Reduced cognitive load through better information grouping",
        ],
      },
      {
        title: "Final Design",
        description:
          "Through multiple rounds of user testing, I refined the design to ensure it effectively communicated opportunity details and improved the quality of applications.",
        insights: [
          "Comprehension improved by 45% in testing",
          "Time to decision decreased by 30%",
          "User confidence in applications increased significantly",
        ],
      },
    ],
  },
  {
    id: 2,
    title: "Limiting Expressions Of Interest",
    category: "Web Design",
    description:
      "Designed intelligent limitation system for legal talent platform to reduce overwhelming submissions from high-volume users while maintaining quality engagement and platform efficiency.",
    image:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/e4b6bda74e2f0502fc4f4d54234babde.png",
    tags: [
      "Legal Tech UX",
      "User Flows",
      "UX Research",
      "Prototyping",
      "User Interviews",
      "User Personas",
      "Visual Design",
      "Visual Design",
      "Design System",
    ],
    duration: "6 months",
    role: "Lead Product Designer",
    year: "2023",
    challenge:
      "As Axiom expanded its legal team, the number of expressions of interest increased. Notably, the top 10% of talent accounted for 60% of all submissions.",
    solution:
      "Limit expressions of interest and clearly explain the reason for the restriction, how many opportunities remain, and the steps to express interest again once a new expression of interest becomes available.",
    results:
      "Expressions of interest decreased by 10% allowing talent services to be able to focus on all of the other relationship building activities that make Axiom's white glove service exceptional.",
    digitalTransformation: {
      description:
        "The project focused on transforming a traditional B2B sales process into a modern digital experience. The platform needed to accommodate complex business purchasing workflows while maintaining the simplicity that drives conversion in consumer e-commerce.",
      images: [
        {
          id: "b2b-flow-before",
          url: "https://public.readdy.ai/ai/img_res/9522392bcd4ee1bb8d03cd84ac2ad9f2.jpg",
          title: "Traditional B2B Sales Process",
          description:
            "The original process required multiple touchpoints between sales representatives and buyers, creating friction and delays in the purchasing journey. Complex approval workflows and manual quote generation slowed down conversions significantly.",
        },
        {
          id: "b2b-flow-after",
          url: "https://readdy.ai/api/search-image?query=Streamlined%20B2B%20e-commerce%20user%20flow%2C%20simplified%20digital%20purchasing%20process%2C%20modern%20business%20workflow%20optimization%2C%20efficient%20enterprise%20buying%20journey&width=800&height=500&seq=902&orientation=landscape",
          title: "Streamlined Digital Experience",
          description:
            "The redesigned flow eliminated unnecessary steps while preserving essential business controls. Self-service capabilities were introduced for standard purchases, with escalation paths for complex orders requiring human intervention.",
        },
      ],
    },
    processImages: [
      {
        id: "ecommerce-analytics",
        url: "https://readdy.ai/api/search-image?query=E-commerce%20analytics%20dashboard%20showing%20user%20behavior%20data%2C%20conversion%20funnels%2C%20heat%20maps%20user%20journey%20analysis%2C%20professional%20business%20interface%20data%20visualization%20charts&width=800&height=500&seq=201&orientation=landscape",
        caption: "Data Analysis & User Behavior",
        description:
          "Analyzed user behavior data, conversion funnels, and identified key drop-off points in the purchasing journey.",
      },
      {
        id: "ecommerce-journey",
        url: "https://readdy.ai/api/search-image?query=B2B%20customer%20journey%20mapping%20for%20e-commerce%2C%20user%20flow%20diagrams%20touchpoint%20analysis%20business%20buyer%20personas%20professional%20workflow%20documentation&width=800&height=500&seq=202&orientation=landscape",
        caption: "Customer Visual Design",
        description:
          "Mapped the complete B2B buyer journey to understand decision-making processes and pain points at each stage.",
      },
      {
        id: "ecommerce-design",
        url: "https://readdy.ai/api/search-image?query=E-commerce%20website%20redesign%20mockups%2C%20before%20and%20after%20comparison%20modern%20product%20catalog%20design%20improved%20navigation%2C%20professional%20B2B%20interface%20clean%20layout&width=800&height=500&seq=203&orientation=landscape",
        caption: "Design System & Interface",
        description:
          "Created a comprehensive design system with improved navigation, product discovery, and streamlined checkout experience.",
      },
      {
        id: "ecommerce-testing",
        url: "https://readdy.ai/api/search-image?query=A%2FB%20testing%20results%20for%20e-commerce%20platform%2C%20conversion%20rate%20optimization%20testing%20metrics%20performance%20analytics%20business%20improvement%20charts&width=800&height=500&seq=204&orientation=landscape",
        caption: "User Flows & Optimization",
        description:
          "Conducted extensive A/B testing to validate design decisions and optimize conversion rates across different user segments.",
      },
    ],
    processSteps: [
      {
        title: "Analytics Deep Dive",
        description:
          "Started with comprehensive analysis of existing platform performance, user behavior patterns, and conversion bottlenecks to establish baseline metrics and identify improvement opportunities.",
        insights: [
          "Cart abandonment rate was 68%",
          "Average session duration was only 2.3 minutes",
          "Product search success rate was below 40%",
        ],
      },
      {
        title: "User Interviews",
        description:
          "Conducted interviews with B2B buyers to understand their purchasing processes, decision criteria, and pain points with the current platform experience.",
        insights: [
          "B2B buyers needed bulk pricing visibility",
          "Approval workflows were not supported",
          "Product comparison features were essential but missing",
        ],
      },
      {
        title: "Design & User Flows",
        description:
          "Redesigned the entire platform with focus on B2B-specific needs including bulk ordering, approval workflows, and enhanced product discovery with advanced filtering.",
        insights: [
          "Implemented progressive disclosure for complex product information",
          "Added bulk pricing calculator",
          "Created streamlined approval workflow integration",
        ],
      },
      {
        title: "Testing & Launch",
        description:
          "Rolled out changes incrementally with extensive A/B testing to measure impact on conversion rates and user satisfaction across different customer segments.",
        insights: [
          "Conversion rate improved from 23% to 37%",
          "Average order value increased by 28%",
          "Customer satisfaction improved significantly",
        ],
      },
      {
        title: "Engagement Strategy",
        description:
          "Developed a comprehensive engagement strategy incorporating gamification principles while ensuring educational value remained the primary focus.",
        insights: [
          "Achievement systems needed to reward effort, not just results",
          "Social features required careful moderation",
          "Progress visualization needed to be immediate and clear",
        ],
      },
    ],
    userResearchAnalysis: {
      title: "Research & Analysis",
      description:
        "Conducted comprehensive user research to understand B2B buyer behavior, pain points, and decision-making processes. This phase involved stakeholder interviews, competitive analysis, and data mining to establish a solid foundation for the redesign strategy.",
      images: [
        {
          id: "research-interviews",
          url: "https://readdy.ai/api/search-image?query=Professional%20customer%20interviews%20and%20user%20research%20sessions%2C%20business%20stakeholder%20meetings%20UX%20research%20methodology%20data%20collection%20process%20modern%20office%20environment&width=800&height=500&seq=911&orientation=landscape",
          title: "Data & Stakeholder Research",
          description:
            "Conducted in-depth interviews with key stakeholders and existing customers to understand current pain points, business requirements, and user expectations for the new platform experience.",
        },
        {
          id: "behavioral-analytics",
          url: "https://readdy.ai/api/search-image?query=User%20behavior%20analytics%20dashboard%20customer%20journey%20data%20analysis%20business%20intelligence%20reports%20data%20visualization%20charts%20professional%20analytics%20environment&width=800&height=500&seq=912&orientation=landscape",
          title: "Behavioral Analytics & Data Mining",
          description:
            "Analyzed existing user behavior data, conversion funnels, and customer journey touchpoints to identify specific areas for improvement and validate research findings with quantitative data.",
        },
      ],
    },
  },
  {
    id: 3,
    title: "Sched NPS Score Improvement",
    category: "Web & Mobile Design",
    description:
      "Comprehensive user experience redesign for Sched's event management platform focused on improving Net Promoter Score through enhanced user satisfaction and streamlined workflows.",
    image:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/285dcc506baf51f969dec8b073cd6967.png",
    tags: [
      "Event Platform UX",
      "NPS Research",
      "User Journey",
      "Prototyping",
      "User Flows",
      "User Personas",
      "Visual Design",
      "Design System",
    ],
    duration: "8 months",
    role: "Lead UX Designer",
    year: "2025",
    challenge:
      "While Sched attendees are 50% more likely to run their own events with Sched, an NPS of 11 indicates that overall satisfaction is lagging—suggesting a significant opportunity to improve the user experience and convert more attendees into loyal customers.",
    solution:
      "Redesign the Event Schedule and My Schedule pages to resolve key usability issues on the platform’s most trafficked areas, enhancing navigation, minimizing friction, and driving higher customer satisfaction.",
    results:
      "Achieved a 173% improvement in NPS, raising it from 11 to our goal of 30 by identifying key pain points and implementing strategic design and usability solutions.",
    schedPlatformOverview: {
      description:
        "Sched is a comprehensive event management platform serving over 50,000 events annually. The platform enables organizers to create, manage, and optimize events while providing attendees with personalized scheduling and networking tools. Our NPS improvement initiative focused on addressing core user pain points that were impacting customer satisfaction and retention.",
      images: [
        {
          id: "sched-platform-before",
          url: "https://readdy.ai/api/search-image?query=Event%20management%20platform%20showing%20complex%20interface%20with%20cluttered%20navigation%2C%20overwhelming%20dashboard%2C%20poor%20information%20hierarchy%2C%20outdated%20design%20patterns%20that%20frustrate%20users&width=800&height=500&seq=sched-before-001&orientation=landscape",
          title: "Platform Challenges",
          description:
            "The existing platform suffered from information overload, complex navigation structures, and inconsistent design patterns that created friction in user workflows and contributed to declining satisfaction scores.",
        },
        {
          id: "sched-platform-after",
          url: "https://readdy.ai/api/search-image?query=Modern%20event%20management%20platform%20with%20clean%20interface%2C%20intuitive%20navigation%2C%20streamlined%20workflows%2C%20improved%20information%20hierarchy%2C%20and%20user-friendly%20design%20that%20enhances%20satisfaction&width=800&height=500&seq=sched-after-001&orientation=landscape",
          title: "Redesigned Experience",
          description:
            "The new design prioritized user goals with simplified navigation, clear visual hierarchy, and streamlined workflows that reduced cognitive load and improved task completion rates.",
        },
      ],
    },
    processImages: [
      {
        id: "nps-research",
        url: "https://readdy.ai/api/search-image?query=NPS%20research%20methodology%20showing%20user%20survey%20data%2C%20feedback%20analysis%2C%20customer%20satisfaction%20metrics%2C%20research%20documentation%2C%20professional%20UX%20research%20environment&width=800&height=500&seq=nps-research-001&orientation=landscape",
        caption: "NPS Research & Analysis",
        description:
          "Conducted comprehensive NPS analysis including user surveys, feedback categorization, and correlation analysis to identify key drivers of user dissatisfaction.",
      },
      {
        id: "user-journey-mapping",
        url: "https://readdy.ai/api/search-image?query=Event%20management%20user%20journey%20mapping%20showing%20touchpoints%2C%20pain%20points%2C%20emotional%20journey%2C%20workflow%20analysis%2C%20professional%20UX%20documentation%20with%20sticky%20notes%20and%20diagrams&width=800&height=500&seq=journey-map-001&orientation=landscape",
        caption: "User Journey Analysis",
        description:
          "Mapped complete user journeys for event organizers and attendees to identify friction points and opportunities for experience improvement.",
      },
      {
        id: "design-system",
        url: "https://readdy.ai/api/search-image?query=Design%20system%20components%20for%20event%20management%20platform%2C%20UI%20component%20library%2C%20style%20guide%2C%20design%20tokens%2C%20modern%20SaaS%20interface%20elements%20with%20consistent%20branding&width=800&height=500&seq=design-system-001&orientation=landscape",
        caption: "Design System Development",
        description:
          "Created a comprehensive design system to ensure consistency across all platform touchpoints and improve development efficiency.",
      },
      {
        id: "usability-testing",
        url: "https://readdy.ai/api/search-image?query=Usability%20testing%20session%20for%20event%20management%20platform%2C%20user%20testing%20environment%2C%20task%20completion%20analysis%2C%20feedback%20collection%2C%20professional%20UX%20testing%20setup&width=800&height=500&seq=usability-test-001&orientation=landscape",
        caption: "Usability Testing & Validation",
        description:
          "Conducted extensive usability testing with event organizers and attendees to validate design decisions and measure improvement in user satisfaction.",
      },
    ],
    processSteps: [
      {
        title: "NPS Baseline Analysis",
        description:
          "Established comprehensive baseline metrics by analyzing existing NPS data, user feedback, and support tickets to understand the root causes of user dissatisfaction and identify improvement opportunities.",
        insights: [
          "NPS score of 32 was 28 points below industry average",
          "Mobile experience was the top complaint (34% of negative feedback)",
          "Group 3 - Navigation Feedback complexity caused 67% of user task failures",
        ],
      },
      {
        title: "User Research Deep Dive",
        description:
          "Conducted extensive user research including surveys, interviews, and behavioral analysis to understand user needs, pain points, and expectations for the event management platform.",
        insights: [
          "Event organizers spent 40% more time than expected on basic tasks",
          "Attendees abandoned session scheduling 52% of the time",
          "Mobile users had 3x higher bounce rates than desktop users",
        ],
      },
      {
        title: "Experience Redesign",
        description:
          "Redesigned core user flows with focus on simplification, mobile optimization, and intuitive navigation to address the primary drivers of user dissatisfaction identified in research.",
        insights: [
          "Reduced navigation depth from 5 levels to 3 levels maximum",
          "Implemented mobile-first responsive design principles",
          "Created contextual help and onboarding flows",
        ],
      },
      {
        title: "Continuous Feedback Integration",
        description:
          "Implemented ongoing feedback collection mechanisms and iterative improvement processes to maintain high NPS scores and quickly address emerging user concerns.",
        insights: [
          "Real-time feedback collection increased response rates by 156%",
          "Monthly NPS tracking enabled rapid issue identification",
          "User satisfaction improved consistently over 6-month period",
        ],
      },
    ],
  },
];

export const portfolioItems = [
  {
    id: 1,
    title: "E-commerce Platform Redesign",
    description: "Complete redesign of a multi-vendor marketplace focusing on user experience and conversion optimization.",
    image: "https://readdy.ai/api/search-image?query=Modern%20e-commerce%20platform%20interface%20with%20clean%20product%20grid%20layout%2C%20shopping%20cart%20functionality%2C%20and%20user-friendly%20navigation%2C%20featuring%20purple%20and%20pink%20gradient%20accents%2C%20professional%20web%20design%20with%20high%20conversion%20optimization&width=600&height=400&seq=ecommerce-redesign-001&orientation=landscape",
    tags: ["E-commerce", "UX/UI Design", "Conversion Optimization", "User Research"],
    category: "Web Design",
    details: {
      client: "TechMart Solutions",
      duration: "4 months",
      role: "Lead UX/UI Designer",
      challenge: "The existing platform had a 68% cart abandonment rate and poor user engagement. Users struggled with navigation and the checkout process was overly complex.",
      solution: "Implemented a user-centered design approach with simplified navigation, streamlined checkout process, and improved product discovery features.",
      results: [
        "Reduced cart abandonment by 45%",
        "Increased conversion rate by 32%",
        "Improved user satisfaction score from 3.2 to 4.7",
        "Generated 28% more revenue in first quarter"
      ],
      images: [
        "https://readdy.ai/api/search-image?query=Before%20and%20after%20comparison%20of%20e-commerce%20homepage%20showing%20improved%20layout%2C%20better%20product%20presentation%2C%20and%20cleaner%20navigation%20with%20purple%20gradient%20elements&width=800&height=500&seq=ecommerce-before-after-001&orientation=landscape",
        "https://readdy.ai/api/search-image?query=Mobile%20responsive%20e-commerce%20design%20showing%20product%20pages%2C%20shopping%20cart%2C%20and%20checkout%20flow%20with%20modern%20UI%20elements%20and%20purple%20accent%20colors&width=800&height=500&seq=ecommerce-mobile-001&orientation=landscape",
        "https://readdy.ai/api/search-image?query=User%20journey%20mapping%20and%20wireframes%20for%20e-commerce%20platform%20showing%20research%20process%2C%20user%20personas%2C%20and%20design%20iterations%20with%20professional%20presentation&width=800&height=500&seq=ecommerce-research-001&orientation=landscape"
      ]
    }
  },
  {
    id: 3,
    title: "FinTech Dashboard",
    description: "Comprehensive financial dashboard for investment tracking and portfolio management.",
    image: "https://readdy.ai/api/search-image?query=Professional%20financial%20technology%20dashboard%20interface%20with%20investment%20charts%2C%20portfolio%20analytics%2C%20trading%20data%20visualization%2C%20and%20modern%20fintech%20UI%20design%20featuring%20purple%20and%20pink%20gradient%20elements&width=600&height=400&seq=fintech-dashboard-001&orientation=landscape",
    tags: ["FinTech", "Data Visualization", "Dashboard Design", "User Experience"],
    category: "Web Design",
    details: {
      client: "InvestSmart Capital",
      duration: "5 months",
      role: "UX/UI Designer",
      challenge: "Investment managers needed a comprehensive tool to track multiple portfolios, analyze market trends, and make data-driven decisions quickly.",
      solution: "Created an intuitive dashboard with real-time data visualization, customizable widgets, and advanced filtering capabilities.",
      results: [
        "50% reduction in decision-making time",
        "Increased user engagement by 65%",
        "99.9% system uptime achieved",
        "Positive feedback from 92% of users"
      ],
      images: [
        "https://readdy.ai/api/search-image?query=Financial%20dashboard%20showing%20real-time%20market%20data%2C%20investment%20charts%2C%20portfolio%20performance%20metrics%2C%20and%20trading%20interface%20with%20professional%20fintech%20design&width=800&height=500&seq=fintech-main-001&orientation=landscape",
        "https://readdy.ai/api/search-image?query=Data%20visualization%20and%20analytics%20charts%20for%20financial%20dashboard%20showing%20market%20trends%2C%20portfolio%20allocation%2C%20and%20investment%20performance%20with%20modern%20UI%20design&width=800&height=500&seq=fintech-charts-001&orientation=landscape",
        "https://readdy.ai/api/search-image?query=User%20research%20and%20testing%20process%20for%20fintech%20application%20showing%20user%20interviews%2C%20usability%20testing%20sessions%2C%20and%20design%20iteration%20workflow&width=800&height=500&seq=fintech-research-001&orientation=landscape"
      ]
    }
  }
];

export const skills = [
  { name: "User Experience Design", level: 95 },
  { name: "User Interface Design", level: 92 },
  { name: "Visual Design", level: 90 },
  { name: "User Flows", level: 90 },
  { name: "User Interviews", level: 88 },
  { name: "Interaction Design", level: 87 },
  { name: "Visual Design", level: 87 },
  { name: "Design Systems", level: 85 },
  { name: "Usability Testing", level: 83 },
  { name: "Copywriting", level: 80 },
  { name: "Information Architecture", level: 80 },
];

export const tools = [
  "Figma",
  "Sketch",
  "Adobe Creative Suite",
  "InVision",
  "Miro",
  "Hotjar",
  "Jira",
  "Notion",
  "Microsoft Office",
];

export const testimonials = [
  {
    id: 1,
    name: "Matt Snyder",
    role: "VP of Product Design at Axiom",
    content:
      "As a product designer at Axiom, Wade led design efforts for 1 of our 3 digital platform experiences. He created a strong partnership with his product manager and added business and user value to all the features he worked on and the problems he solved. Through user research, data, and a strong understanding of business process, Wade took that area of our product from 0-100. Wade's hard work and determination is hard to find. His positive attitude helped push our team forward as our team and department grew over the last 2 years!",
    avatar:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/968110452452bf4aaef6cfc51f131185.png",
  },
  {
    id: 2,
    name: "John Hurr",
    role: "Director of User Experience at Sonos",
    content:
      "Wade has an excellent attitude and produces high quality work. He works well independently and with others. He has a great eye for design and sketches out most ideas before execution - a definite plus. Wade is a solid designer, I would recommend him to anyone.",
    avatar:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/6d9f19e2317c14f5d064ee6d3201eaf1.png",
  },
  {
    id: 3,
    name: "Yevhen Tsvietukhin",
    role: "Head of Product at Sched",
    content:
      "Wade's collaborative approach and design expertise made him an invaluable team member. His work consistently exceeded expectations.",
    avatar:
      "https://static.readdy.ai/image/bc549a5ff59a064e0762914cf4e0532d/2e48deea3f711ac3f6fe63f34777e818.png",
  },
];
